import { ChevronRight } from 'lucide-react';
import { type ForwardedRef, forwardRef } from 'react';
import styles from './ContextMenu.module.css';
import type { MenuSubNode } from './types';

export type ContextMenuSubProps = {
  node: MenuSubNode;
  active: boolean;
  /** Единственный табируемый пункт панели (roving tabindex). */
  tabbable: boolean;
  expanded: boolean;
  onOpenSub: () => void;
  onHover: () => void;
};

/**
 * Пункт-раскрыватель подменю (макет 66:16 — тот же ряд, но шеврон справа).
 *
 * Саму панель подменю рисует родительский MenuPanel: так уровень вложенности
 * остаётся ровно один (инвариант I4), а позиционирование считается в одном
 * месте, а не расползается по компонентам.
 */
export const ContextMenuSub = forwardRef(function ContextMenuSub(
  { node, active, tabbable, expanded, onOpenSub, onHover }: ContextMenuSubProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = node.icon;
  const disabled = node.disabled;
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      tabIndex={tabbable ? 0 : -1}
      aria-haspopup="menu"
      aria-expanded={expanded}
      aria-disabled={disabled ? true : undefined}
      {...(disabled ? { title: disabled.reason, 'aria-describedby': `${node.id}-why` } : {})}
      className={[
        styles.item,
        disabled ? styles.itemDisabled : '',
        active || expanded ? styles.itemActive : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={onHover}
      onClick={() => {
        if (disabled) return;
        onOpenSub();
      }}
    >
      <span className={styles.icon} aria-hidden="true">
        {Icon ? <Icon size={14} /> : null}
      </span>
      <span className={styles.label}>{node.label}</span>
      {node.meta && <span className={styles.meta}>{node.meta}</span>}
      {disabled?.note && <span className={styles.note}>{disabled.note}</span>}
      {/* Полная причина — в `title` и скрытом описании, как у обычного пункта. */}
      {disabled && (
        <span className={styles.srOnly} id={`${node.id}-why`}>
          {disabled.reason}
        </span>
      )}
      <span className={styles.chevron} aria-hidden="true">
        <ChevronRight size={14} />
      </span>
    </button>
  );
});
